import { useEffect, useRef, useState } from "react";
import { Cloud, RefreshCw } from "lucide-react";
import { getLocale, t } from "../../i18n";
import { gatewayState, type GatewayState, type PrintGatewayStatus } from "../../gatewayStatus";

type Props = { onLoad: () => Promise<PrintGatewayStatus | null> };

const COPY: Record<GatewayState | "error" | "direct", [string, string]> = {
  direct: ["Εκτύπωση διακομιστή", "Η εκτύπωση μέσω διακομιστή έχει ρυθμιστεί. Η σύνδεση κάθε εκτυπωτή ελέγχεται ξεχωριστά."],
  relay: ["Αναμετάδοση Android", "Η εκτύπωση χρειάζεται τη ρυθμισμένη συσκευή Android στο δίκτυο του καταστήματος."],
  configured: ["Πύλη καταστήματος", "Η πύλη έχει ρυθμιστεί. Δεν υπάρχουν πρόσφατα στοιχεία σύνδεσης."],
  online: ["Η πύλη είναι συνδεδεμένη", "Τα δελτία δρομολογούνται μέσω της πύλης του καταστήματος. Η σύνδεση κάθε εκτυπωτή ελέγχεται ξεχωριστά."],
  offline: ["Χωρίς πρόσφατη σύνδεση πύλης", "Ελέγξτε την τροφοδοσία και τη σύνδεση Internet της πύλης και δοκιμάστε ξανά."],
  unavailable: ["Η υπηρεσία πύλης δεν είναι διαθέσιμη", "Δεν ήταν δυνατός ο έλεγχος της πύλης. Επικοινωνήστε με τον διαχειριστή της εγκατάστασης."],
  error: ["Η κατάσταση εκτύπωσης δεν είναι διαθέσιμη", "Ελέγξτε τη σύνδεση με την υπηρεσία και ανανεώστε την κατάσταση."]
};

export function GatewayStatusCard({ onLoad }: Props) {
  const [info, setInfo] = useState<PrintGatewayStatus | null>(null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [checkedAt, setCheckedAt] = useState(0);
  const refreshRef = useRef<() => void>(() => {});

  useEffect(() => {
    let cancelled = false;
    let pending = false;
    let unsupported = false;
    const refresh = async () => {
      if (pending || unsupported) return;
      pending = true;
      setBusy(true);
      try {
        const next = await onLoad();
        unsupported = next === null;
        if (!cancelled) {
          setInfo(next);
          setFailed(false);
          setCheckedAt(Date.now());
        }
      } catch {
        if (!cancelled) {
          setInfo(null);
          setFailed(true);
          setCheckedAt(Date.now());
        }
      } finally {
        pending = false;
        if (!cancelled) setBusy(false);
      }
    };
    refreshRef.current = () => { void refresh(); };
    const whenVisible = () => { if (document.visibilityState === "visible") void refresh(); };
    void refresh();
    const timer = window.setInterval(whenVisible, 30_000);
    document.addEventListener("visibilitychange", whenVisible);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", whenVisible);
    };
  }, [onLoad]);

  // Older servers have no gateway endpoint; don't show an empty/unsupported setting.
  if (!info && !failed) return null;
  const state = failed ? "error" : gatewayState(info!);
  const [title, description] = COPY[state === "configured" && info?.mode === "direct" ? "direct" : state];
  return (
    <section className="settings-group gateway-status-card" aria-label={t("Εκτύπωση καταστήματος")} aria-busy={busy}>
      <div className="gateway-status-head">
        <span className="gateway-status-icon"><Cloud /></span>
        <div className="gateway-status-copy">
          <h2>{t("Εκτύπωση καταστήματος")}</h2>
          <p className="gateway-status-label" data-state={state} role="status">{t(title)}</p>
        </div>
      </div>
      <p className="gateway-status-description">{t(description)}</p>
      <div className="gateway-status-footer">
        <small>{t("Τελευταίος έλεγχος: {0}", new Date(checkedAt).toLocaleTimeString(getLocale(), { hour: "2-digit", minute: "2-digit", second: "2-digit" }))}</small>
        <button type="button" className="secondary-command" disabled={busy} onClick={() => refreshRef.current()}>
          <RefreshCw /><span>{t(busy ? "Έλεγχος..." : "Ανανέωση κατάστασης")}</span>
        </button>
      </div>
    </section>
  );
}
